/**
 * Episodes for the show the sheet is showing.
 *
 * Cached in IndexedDB, because the cost of not caching is visible: the sheet
 * begins its slide-up animation while the request is still in flight, so it
 * animated open at "loading" size and then resized mid-flight when the season
 * list arrived about 200ms later. Serving the cache first means the sheet has
 * its content before it starts moving.
 *
 * The cache is refreshed in the background on every open. Episode lists change
 * when a season is announced or a title is corrected — worth picking up, never
 * worth waiting for.
 */
import { useEffect, useState } from "react";

import type { SimklClient } from "@/api/client";
import { parseEpisodes } from "@/lib/airtime";
import type { Episode } from "@/lib/airtime";
import { EPISODES_STORE, withStore } from "@/store/db";

export interface ShowEpisodes {
  episodes: readonly Episode[];
  loading: boolean;
  error: boolean;
}

interface CacheRow {
  simklId: number;
  episodes: Episode[];
  fetchedAt: number;
}

interface Loaded {
  simklId: number;
  episodes: readonly Episode[];
  error: boolean;
}

const NONE: readonly Episode[] = [];

async function readCache(simklId: number): Promise<Episode[] | null> {
  try {
    const [row] = await withStore<CacheRow | undefined>(
      EPISODES_STORE,
      "readonly",
      (store) => store.get(simklId) as IDBRequest<CacheRow | undefined>,
    );
    return row?.episodes ?? null;
  } catch {
    return null;
  }
}

async function writeCache(simklId: number, episodes: Episode[]): Promise<void> {
  try {
    await withStore(EPISODES_STORE, "readwrite", (store) =>
      store.put({ simklId, episodes, fetchedAt: Date.now() } satisfies CacheRow),
    );
  } catch {
    // A full or unavailable store costs a little speed, never correctness.
  }
}

export function useShowEpisodes(client: SimklClient, simklId: number | null): ShowEpisodes {
  const [loaded, setLoaded] = useState<Loaded | null>(null);

  useEffect(() => {
    if (simklId === null) return;
    const controller = new AbortController();

    void (async () => {
      const cached = await readCache(simklId);
      if (controller.signal.aborted) return;
      if (cached !== null && cached.length > 0) {
        setLoaded({ simklId, episodes: cached, error: false });
      }
      try {
        const episodes = parseEpisodes(await client.episodes(simklId, controller.signal));
        if (controller.signal.aborted) return;
        setLoaded({ simklId, episodes, error: false });
        await writeCache(simklId, episodes);
      } catch {
        // Only surface the failure when there was nothing cached to show.
        if (!controller.signal.aborted && cached === null) {
          setLoaded({ simklId, episodes: NONE, error: true });
        }
      }
    })();

    return () => {
      controller.abort();
    };
  }, [client, simklId]);

  // Tagging the result with its show means the previous show's episodes can
  // never appear under a new header: a result for another id does not match.
  const fresh = loaded !== null && loaded.simklId === simklId;
  return {
    episodes: fresh ? loaded.episodes : NONE,
    loading: simklId !== null && !fresh,
    error: fresh && loaded.error,
  };
}
